import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideKeyRound } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, catchError, merge, of, switchMap, timer } from 'rxjs';
import { MulesoftService } from '../../../features/home/services/mulesoft.service';

/** Header button showing whether the SAP (MuleSoft) token is valid; clicking it requests a new one. */
@Component({
  selector: 'app-sap-token-button',
  templateUrl: './sap-token-button.html',
  imports: [NgIcon, HlmButtonImports, HlmTooltipImports, TranslatePipe],
  providers: [provideIcons({ lucideKeyRound })],
})
export class SapTokenButton implements OnInit {
  private readonly mulesoftService = inject(MulesoftService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tokenValidationIntervalMs = 3 * 60 * 1000;
  private readonly tokenGenerated$ = new Subject<void>();
  private tokenChannel?: BroadcastChannel;
  public readonly isTokenValid = signal<boolean>(false);
  public readonly tokenStatus = signal<string>('Active');
  public readonly tokenExpiresIn = signal<string>('1h');
  public readonly tooltip = computed(() =>
    `SAP Token: ${this.isTokenValid() ? 'Active' : 'Inactive'} (Expires: ${this.tokenExpiresIn()})`);

  ngOnInit(): void {
    this.listenForTokenGeneration();

    merge(
      timer(60 * 1000, this.tokenValidationIntervalMs),
      this.tokenGenerated$,
    )
      .pipe(
        switchMap(() =>
          this.mulesoftService.ValidTokens().pipe(catchError(() => of(null)))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        if (!response) {
          this.isTokenValid.set(false);
          this.tokenStatus.set('Inactive');
          this.tokenExpiresIn.set('N/A');
          sessionStorage.setItem('isTokenValid', JSON.stringify(false));
          return;
        }

        sessionStorage.setItem('isTokenValid', JSON.stringify(response.isValidToken));
        this.isTokenValid.set(response.isValidToken);
        this.tokenStatus.set(response.isValidToken ? 'Active' : 'Inactive');
        this.tokenExpiresIn.set(
          response.expiresTime != null ? `${response.expiresTime}min` : 'N/A'
        );
      });
  }

  private listenForTokenGeneration(): void {
    try {
      this.tokenChannel = new BroadcastChannel('sap-token');
      this.tokenChannel.onmessage = () => this.tokenGenerated$.next();
      this.destroyRef.onDestroy(() => this.tokenChannel?.close());
    } catch {
      // BroadcastChannel not available - fall back to interval only
    }
  }

  generateSamlAssertion(): void {
    this.mulesoftService.getToken('deviation');
  }
}
